import {
  Box,
  Center,
  Fade,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';

import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChevronDown, Info, Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { UnitsOfCompetencyTable } from './UnitsOfCompetencyTable';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { Controller, useForm } from 'react-hook-form';
import { unitsOfCompetencyTableFiltersSchema } from './unitsOfCompetencyTableFilters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UnitContextCategoryModal } from './UnitContextCategoryModal';
import { withPermission } from '../withPermission';
import { useCheckPermission } from '../hooks/useCheckPermission';

export const UnitsOfCompetency = withPermission('canManageCourseContent', () => {
  const { currentPage, pageSize, setPage } = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const hasAIContentGenerationPermission = useCheckPermission('canAccessAIContentGeneration');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [filters, setFilters] = useState(() => {
    return unitsOfCompetencyTableFiltersSchema.parse({
      search: searchParams.get('search') ?? '',
      sort: searchParams.get('sort') ?? undefined,
      orderBy: searchParams.get('orderBy') ?? undefined,
    });
  });

  const handleFiltersChange = (newFilters: Partial<typeof filters>) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value?.length > 0) {
            nextParams.set(key, value);
          } else {
            nextParams.delete(key);
          }
        });

        return nextParams;
      },
      { replace: true },
    );
  };

  const handleSearchCommit = (v: string) => {
    handleFiltersChange({ search: v.trim() });
  };

  const { data, isPending, refetch } = trpcAdmin.unitsOfCompetency.all.useQuery({
    search: filters.search,
    filters: {
      sort: filters.sort,
      orderBy: filters.orderBy,
    },
    pagination: {
      page: Number(currentPage),
      limit: pageSize,
    },
  });

  const { data: contextCategories, refetch: refetchCategories } =
    trpcAdmin.unitContextCategories.all.useQuery();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    handleSubmit,
    register,
    clearErrors,
    getValues,
    setValue,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting, isLoading },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: {
      title: '',
      code: '',
      description: '',
      nominalHours: 0,
      volumeOfLearningHours: 0,
      vetFlag: false,
      subjectFieldOfEducation: '',
      unitContextCategoryId: null,
    },
  });

  // Watch the value of vetFlag
  const vetFlag = watch('vetFlag', false);

  const createUnitOfCompetency = trpcAdmin.unitsOfCompetency.create.useMutation({
    onSuccess: () => {
      toast({
        title: t`Unit of Competency created`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
      void refetch();
      reset();
    },
    onError: (error) => {
      toast({
        title: t`Error creating Unit of Competency`,
        description: error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const onSubmit = (data: {
    title: string;
    code: string;
    nominalHours: number;
    volumeOfLearningHours: number;
    subjectFieldOfEducation: string;
    vetFlag: boolean;
    description?: string;
    unitContextCategoryId: string | null;
  }) => {
    createUnitOfCompetency.mutate({
      ...data,
      description: data.description ?? undefined,
    });
  };

  const unitsOfCompetency =
    data && 'pagination' in data
      ? data
      : {
          items: [],
          pagination: {
            currentPage: 1,
            previousPage: null,
            nextPage: null,
            pageCount: 0,
            totalCount: 0,
          },
        };

  const deleteUnitOfCompetency = trpcAdmin.unitsOfCompetency.archive.useMutation({
    onSuccess: () => {
      void refetch();
    },
    onError: (error) => {
      toast({
        title: t`Unable to delete Unit of Competency`,
        description: error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const handleClose = (): void => {
    onClose();
    reset();
  };

  return (
    <>
      <TableViewLayout
        pageHeader={
          <PageHeading>
            <Trans>Units of Competency</Trans>
          </PageHeading>
        }
        pageActions={
          <HStack spacing={2}>
            {hasAIContentGenerationPermission ? (
              <Button
                leftIcon={<Icon as={Sparkles} />}
                size="sm"
                variant="primary"
                onClick={() => navigate('/units/generation')}
              >
                <Trans>Add a Unit of Competency using AI</Trans>
              </Button>
            ) : null}
            <Button
              leftIcon={<Icon as={Plus} />}
              size="sm"
              variant={hasAIContentGenerationPermission ? 'outline' : 'primary'}
              onClick={onOpen}
            >
              <Trans>Add a Unit of Competency</Trans>
            </Button>
          </HStack>
        }
        filters={<SearchInput maxW="search-input" onCommit={handleSearchCommit} />}
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={unitsOfCompetency?.pagination.totalCount ?? 0}
              zero="0 units"
              one="1 unit"
              other="# units"
            />
          </Text>
        }
        table={
          <>
            <UnitsOfCompetencyTable
              unitsOfCompetency={unitsOfCompetency.items}
              handleUnitDelete={deleteUnitOfCompetency.mutate}
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
            {!isPending && unitsOfCompetency.items.length === 0 && (
              <Center p={8}>
                <Trans>No units found</Trans>
              </Center>
            )}
            {isPending && data === undefined ? (
              <Fade
                in={true}
                transition={{
                  enter: {
                    delay: 0.3,
                    duration: 0,
                  },
                }}
              >
                <Center p={8}>
                  <Spinner size="xl" transitionDelay={'2900ms'} />
                </Center>
              </Fade>
            ) : null}
          </>
        }
        pagination={
          unitsOfCompetency.items.length > 0 ? (
            <Pagination pagination={unitsOfCompetency.pagination} onPageChange={setPage} />
          ) : null
        }
      />

      <Modal isOpen={isOpen} onClose={handleClose} isCentered size={'2xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Create a new Unit of Competency</Trans>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={!!errors.title}>
                  <FormLabel>
                    <Trans>Title</Trans>
                  </FormLabel>
                  <Input
                    autoFocus
                    type="text"
                    {...register('title', {
                      required: t`Title is required`,
                      onChange: () => {
                        if (errors.title) {
                          clearErrors('title');
                        }
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.code}>
                  <FormLabel>
                    <Trans>Code</Trans>
                  </FormLabel>
                  <Input
                    type="text"
                    autoComplete="off"
                    spellCheck="false"
                    {...register('code', {
                      required: t`Unit code is required`,
                      onChange: () => {
                        if (errors.code) {
                          clearErrors('code');
                        }
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
                </FormControl>

                <VStack gap="8px" w="full" py="8px">
                  <HStack w="full" justify="space-between" align="start" gap="10px">
                    <FormControl isInvalid={!!errors.nominalHours}>
                      <FormLabel>
                        <Trans>Nominal hours</Trans>{' '}
                        <Tooltip
                          label={t`The supervised hours assigned to this unit by the training package.`}
                          placement="top"
                        >
                          <Box as="span" display="inline-block" verticalAlign="middle">
                            <Info size={14} />
                          </Box>
                        </Tooltip>
                      </FormLabel>
                      <NumberInput
                        min={0}
                        max={500}
                        step={5}
                        defaultValue={getValues('nominalHours')}
                        onChange={(_valueAsString, valueAsNumber) => {
                          setValue('nominalHours', valueAsNumber, {
                            shouldValidate: true,
                          });
                        }}
                      >
                        <NumberInputField
                          {...register('nominalHours', {
                            valueAsNumber: true,
                            validate: (value) => {
                              if (vetFlag && (!value || value <= 0)) {
                                return t`Nominal hours are required when the unit is accredited.`;
                              }
                              return true;
                            },
                          })}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <FormErrorMessage>{errors.nominalHours?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!errors.volumeOfLearningHours}>
                      <FormLabel>
                        <Trans>Volume of learning hours</Trans>{' '}
                        <Tooltip
                          label={t`Total learning hours including supervised, unsupervised, and assessment activities.`}
                          placement="top"
                        >
                          <Box as="span" display="inline-block" verticalAlign="middle">
                            <Info size={14} />
                          </Box>
                        </Tooltip>
                      </FormLabel>
                      <NumberInput
                        min={0}
                        step={5}
                        defaultValue={getValues('volumeOfLearningHours')}
                        onChange={(_valueAsString, valueAsNumber) => {
                          setValue('volumeOfLearningHours', valueAsNumber, {
                            shouldValidate: true,
                          });
                        }}
                      >
                        <NumberInputField
                          {...register('volumeOfLearningHours', {
                            valueAsNumber: true,
                            validate: (value) => {
                              if (value != null && typeof value === 'number' && value < 0) {
                                return t`Volume of learning hours must be zero or greater.`;
                              }
                              return true;
                            },
                          })}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <FormErrorMessage>{errors.volumeOfLearningHours?.message}</FormErrorMessage>
                    </FormControl>
                  </HStack>
                </VStack>

                <FormControl isInvalid={!!errors.subjectFieldOfEducation}>
                  <FormLabel>
                    <Trans>Subject of education</Trans>
                  </FormLabel>
                  <Input
                    type="text"
                    autoComplete="Off"
                    {...register('subjectFieldOfEducation', {
                      validate: (value) => {
                        if (vetFlag && !value) {
                          return t`Subject of education is required when the unit is accredited.`;
                        }
                        if (value && !/^[a-zA-Z0-9]{6}$/.test(value)) {
                          return t`Subject of education must be a 6-character alphanumeric string.`;
                        }
                        return true;
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.subjectFieldOfEducation?.message}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <Trans>Description</Trans>
                  </FormLabel>
                  <Textarea
                    autoComplete="off"
                    spellCheck="false"
                    size="sm"
                    {...register('description')}
                  />
                </FormControl>

                <FormControl id="unitContextCategoryId">
                  <FormLabel htmlFor="unitContextCategoryId">
                    <Trans>Context category</Trans>
                  </FormLabel>
                  <Controller
                    name="unitContextCategoryId"
                    control={control}
                    render={({ field }) => {
                      const selectedOption = contextCategories?.find(
                        (category) => category.id === field.value,
                      );

                      return (
                        <>
                          <Menu matchWidth>
                            <MenuButton
                              as={Button}
                              variant="outline"
                              px={2}
                              width="full"
                              colorScheme="gray"
                              textAlign="left"
                              rightIcon={<ChevronDown />}
                            >
                              {selectedOption ? selectedOption.categoryName : t`Please select`}
                            </MenuButton>
                            <MenuList maxH="300px" overflowY="auto" paddingBottom="0">
                              {contextCategories?.map((category) => (
                                <MenuItem
                                  key={category.id}
                                  onClick={() => field.onChange(category.id)}
                                >
                                  {category.categoryName}
                                </MenuItem>
                              ))}
                              <MenuItem
                                onClick={() => setIsModalOpen(true)}
                                icon={<Plus />}
                                position="sticky"
                                bottom="0"
                                zIndex="1"
                                boxShadow="sm"
                              >
                                <Trans>Add or update categories</Trans>
                              </MenuItem>
                            </MenuList>
                          </Menu>

                          <UnitContextCategoryModal
                            isOpen={isModalOpen}
                            onClose={() => setIsModalOpen(false)}
                            contextCategories={contextCategories ?? []}
                            refetchCategories={refetchCategories}
                          />
                        </>
                      );
                    }}
                  />
                </FormControl>

                <FormControl>
                  <HStack alignItems="center">
                    <FormLabel>
                      <Trans>Is this unit of competency accredited?</Trans>{' '}
                      <Tooltip
                        label={t`When on, this unit is included in AVETMISS reports. Turn off for non-vocational units.`}
                        placement="top"
                      >
                        <Box as="span" display="inline-block" verticalAlign="middle">
                          <Info size={14} />
                        </Box>
                      </Tooltip>
                    </FormLabel>
                    <Switch {...register('vetFlag')} colorScheme="brand" />
                  </HStack>
                </FormControl>

                <Flex justify={'flex-end'}>
                  <Button
                    right={0}
                    type="submit"
                    isLoading={isSubmitting}
                    isDisabled={createUnitOfCompetency.isPending || isSubmitting || isLoading}
                    loadingText="Submitting"
                  >
                    <Trans>Create</Trans>
                  </Button>
                </Flex>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
});
